import React, { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';

import { useTheme } from '../theme/ThemeContext';
import { usePermissions } from '../context/PermissionContext';
import { ROUTES } from '../constants/routes';
import useSurgeryStore from '../stores/surgeryStore';
import useDepartmentStore from '../stores/departmentStore';
import AdminPageHeader from '../components/common/AdminPageHeader';
import FeesBillingTabs from '../components/settings/FeesBillingTabs';
import { ConfirmationModal } from '../components/common';
import { GST_RATES } from '../constants/bedCategory';
import { ORG_WIDE_FILTER, filterByDeptMembership } from '../utils/deptMembershipFilter';
import './ProcedureCatalogPage.css';

// B2 / S4–S5 — Surgery/Operation/Delivery catalog. Clone of the admin-catalog
// pattern (org-global — no dept machinery). `billing_mode` is THE cross-field
// driver: PACKAGE shows+requires package_price (+inclusions) and hides the
// itemized components; ITEMIZED hides package_price and requires ≥1 component.
// Component rows DERIVE from the selected roles (both directions); toggling
// the mode PRESERVES hidden-side values and validates the ACTIVE mode only.
// The server stays authoritative — its machine-readable 422 codes
// (PACKAGE_PRICE_REQUIRED / ITEMIZED_NEEDS_COMPONENT) map to field errors.

const COMPONENT_ROLES = [
  { value: 'surgeon', label: 'Surgeon', provider: true },
  { value: 'assistant', label: 'Assistant', provider: true },
  { value: 'anaesthetist', label: 'Anaesthetist', provider: true },
  { value: 'ot_charge', label: 'OT charge', provider: false },
];

const BILLING_MODES = [
  { value: 'PACKAGE', label: 'Package' },
  { value: 'ITEMIZED', label: 'Itemized' },
];

const blankRow = () => ({
  name: '',
  specialty: '',
  billing_mode: 'PACKAGE',
  package_price: '',
  inclusions: '',      // comma-separated in the form; list[str] on the wire
  implants_billable: false,
  gst_rate: 0,
  requires_consent: false,
  duration: '',
  active: true,
});

const blankPrices = () => ({ surgeon: '', assistant: '', anaesthetist: '', ot_charge: '' });

function parseList(text) {
  return String(text || '')
    .split(',')
    .map((a) => a.trim())
    .filter(Boolean);
}

function validate(row, selectedRoles, componentPrices) {
  const e = {};
  if (!row.name || !row.name.trim()) e.name = 'Required';
  if (row.billing_mode === 'PACKAGE') {
    // Active mode = PACKAGE → only package fields are required.
    if (row.package_price === '' || row.package_price === null || row.package_price === undefined) {
      e.package_price = 'Required for package billing';
    } else if (Number.isNaN(Number(row.package_price)) || Number(row.package_price) < 0) {
      e.package_price = 'Must be 0 or more';
    }
  } else {
    // Active mode = ITEMIZED → ≥1 priced component; package fields ignored.
    if (selectedRoles.length === 0) {
      e.components = 'Add at least one component';
    } else {
      for (const role of selectedRoles) {
        const price = componentPrices[role];
        if (price === '' || Number.isNaN(Number(price)) || Number(price) < 0) {
          e.components = 'Every selected component needs a price of 0 or more';
          break;
        }
      }
    }
  }
  if (row.duration !== '' && (!Number.isInteger(Number(row.duration)) || Number(row.duration) < 0)) {
    e.duration = 'Must be a whole number of minutes';
  }
  return e;
}

export default function SurgeryCatalogPage() {
  const { isDark } = useTheme();
  const { hasPermission } = usePermissions();

  const {
    surgeries,
    isLoading,
    error,
    includeInactive,
    fetchSurgeries,
    createSurgery,
    updateSurgery,
    deactivateSurgery,
    setIncludeInactive,
    setSurgeryDepartments,
    clearError,
  } = useSurgeryStore();

  // B5 / S1 — org departments for the "Visible to" tag field (clone of A4/A5).
  // Only ACTIVE departments are offered. `specialty` (below) is a free-text
  // clinical label, orthogonal to this visibility tag.
  const departments = useDepartmentStore((s) => s.departments);
  const fetchDepartments = useDepartmentStore((s) => s.fetchDepartments);
  const activeDepts = useMemo(
    () => (departments || []).filter((d) => d.active !== false)
      .map((d) => ({ dept_id: d.dept_id || d.id, name: d.name })),
    [departments],
  );
  const deptNameById = useMemo(() => {
    const m = {};
    for (const d of activeDepts) m[d.dept_id] = d.name;
    return m;
  }, [activeDepts]);

  const [form, setForm] = useState(null); // null | {mode:'add'} | {mode:'edit', id}
  const [row, setRow] = useState(blankRow());
  const [orig, setOrig] = useState(null);
  // Itemized side — component rows derive from the selected roles.
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [componentPrices, setComponentPrices] = useState(blankPrices());
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [banner, setBanner] = useState(null);
  // B5 — dept-tag selection for the current form; saved via the PUT in the same
  // Save click. `deptFilter` is the display-only list view-filter.
  const [deptIds, setDeptIds] = useState([]);
  const [deptFilter, setDeptFilter] = useState('');

  // Initial load — stable deps, single-flight stores (bounded requests).
  useEffect(() => {
    fetchSurgeries();
    fetchDepartments();
  }, [fetchSurgeries, fetchDepartments]);

  const change = (k, v) => setRow((r) => ({ ...r, [k]: v }));
  const toggleDept = (deptId) => {
    setDeptIds((prev) =>
      prev.includes(deptId) ? prev.filter((d) => d !== deptId) : [...prev, deptId]
    );
  };

  // ── B5 (bug 3) — display-only list view-filter, MEMBERSHIP semantic: a dept
  // anchor shows ONLY surgeries tagged to it; "Org-wide" shows ONLY untagged;
  // empty = full list. Distinct from the VISIBILITY union used by consumption
  // pickers. ──────
  const visibleSurgeries = useMemo(
    () => filterByDeptMembership(surgeries, deptFilter),
    [surgeries, deptFilter],
  );

  const toggleRole = (role) => {
    setSelectedRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
    );
  };

  const openAdd = () => {
    setRow(blankRow());
    setOrig(null);
    setSelectedRoles([]);
    setComponentPrices(blankPrices());
    setErrors({});
    setBanner(null);
    setDeptIds([]);
    setForm({ mode: 'add' });
  };

  const openEdit = (p) => {
    // Seed ONLY form fields — read-only/derived keys (surgery_id,
    // organization_id, is_preset, timestamps) never enter form state.
    setRow({
      name: p.name || '',
      specialty: p.specialty || '',
      billing_mode: p.billing_mode || 'PACKAGE',
      package_price: p.package_price ?? '',
      inclusions: (p.inclusions || []).join(', '),
      implants_billable: !!p.implants_billable,
      gst_rate: p.gst_rate ?? 0,
      requires_consent: !!p.requires_consent,
      duration: p.duration ?? '',
      active: p.active !== false,
    });
    const components = Array.isArray(p.components) ? p.components : [];
    setSelectedRoles(components.map((c) => c.role));
    setComponentPrices({
      ...blankPrices(),
      ...Object.fromEntries(components.map((c) => [c.role, c.price ?? ''])),
    });
    setOrig(p);
    setErrors({});
    setBanner(null);
    setDeptIds(Array.isArray(p.department_ids) ? [...p.department_ids] : []);
    setForm({ mode: 'edit', id: p.surgery_id });
  };

  const close = () => {
    setForm(null);
    setOrig(null);
    setErrors({});
    setDeptIds([]);
  };

  const buildComponents = () =>
    selectedRoles.map((role) => ({ role, price: Number(componentPrices[role]) }));
  const buildProviderRoles = () =>
    selectedRoles.filter((r) => COMPONENT_ROLES.find((c) => c.value === r)?.provider);

  const submit = async () => {
    const e = validate(row, selectedRoles, componentPrices);
    setErrors(e);
    if (Object.keys(e).length) return;
    setBusy(true);

    const isPackage = row.billing_mode === 'PACKAGE';
    let result;
    if (form.mode === 'add') {
      // Only the ACTIVE mode's pricing fields travel — hidden-side values are
      // preserved client-side for toggling but never submitted half-filled.
      const body = {
        name: row.name.trim(),
        billing_mode: row.billing_mode,
        gst_rate: Number(row.gst_rate),
        implants_billable: !!row.implants_billable,
        requires_consent: !!row.requires_consent,
      };
      if (row.specialty.trim()) body.specialty = row.specialty.trim();
      if (row.duration !== '') body.duration = Number(row.duration);
      if (isPackage) {
        body.package_price = Number(row.package_price);
        const inclusions = parseList(row.inclusions);
        if (inclusions.length) body.inclusions = inclusions;
      } else {
        body.components = buildComponents();
        body.provider_roles = buildProviderRoles();
      }
      result = await createSurgery(body);
    } else {
      // PATCH = allowlist DIFF vs the original row (read-only keys can't leak).
      const patch = {};
      const changed = (key, newVal, origVal) => {
        if (JSON.stringify(newVal) !== JSON.stringify(origVal)) patch[key] = newVal;
      };
      changed('name', row.name.trim(), orig.name);
      changed('specialty', row.specialty.trim() || null, orig.specialty || null);
      changed('billing_mode', row.billing_mode, orig.billing_mode || 'PACKAGE');
      changed('implants_billable', !!row.implants_billable, !!orig.implants_billable);
      changed('gst_rate', Number(row.gst_rate), Number(orig.gst_rate ?? 0));
      changed('requires_consent', !!row.requires_consent, !!orig.requires_consent);
      changed('duration', row.duration === '' ? null : Number(row.duration), orig.duration ?? null);
      changed('active', row.active !== false, orig.active !== false);
      if (isPackage) {
        changed('package_price', Number(row.package_price), orig.package_price ?? null);
        changed('inclusions', parseList(row.inclusions), orig.inclusions || []);
      } else {
        changed('components', buildComponents(), orig.components || []);
        changed('provider_roles', buildProviderRoles(), orig.provider_roles || []);
      }
      // B5 — a dept-tag-only edit must still save (tags travel via the separate
      // PUT, not the patch). Only "nothing changed at all" is a no-op.
      const seededDepts = Array.isArray(orig.department_ids) ? orig.department_ids : [];
      const deptChanged =
        deptIds.length !== seededDepts.length || deptIds.some((d) => !seededDepts.includes(d));
      if (Object.keys(patch).length === 0 && !deptChanged) {
        setBusy(false);
        setBanner({ kind: 'err', msg: 'No changes to save' });
        return;
      }
      result = Object.keys(patch).length
        ? await updateSurgery(form.id, patch)
        : { success: true, data: {} }; // dept-tag-only change → skip the PATCH
    }

    if (!result.success) {
      setBusy(false);
      // R-B2-5 — map the server's machine-readable code to the field it
      // belongs to (never dump raw prose as the only signal).
      if (result.code === 'PACKAGE_PRICE_REQUIRED') {
        setErrors((prev) => ({ ...prev, package_price: result.error }));
      } else if (result.code === 'ITEMIZED_NEEDS_COMPONENT') {
        setErrors((prev) => ({ ...prev, components: result.error }));
      } else {
        setBanner({ kind: 'err', msg: result.error || 'Save failed' });
      }
      return;
    }
    // B5 — tag departments as part of the SAME Save click, AFTER create/PATCH.
    const isAdd = form.mode === 'add';
    const surgeryId = isAdd ? result.data?.surgery_id : form.id;
    if (!isAdd || deptIds.length > 0) {
      if (!surgeryId) {
        setBusy(false);
        setBanner({ kind: 'err', msg: 'Surgery created, but departments could not be saved — edit it to set departments.' });
        close();
        return;
      }
      const deptResult = await setSurgeryDepartments(surgeryId, deptIds);
      setBusy(false);
      if (!deptResult.success) {
        setBanner({ kind: 'err', msg: `Surgery ${isAdd ? 'created' : 'updated'}, but departments were not saved: ${deptResult.error || 'unknown error'}` });
        return;
      }
    } else {
      setBusy(false);
    }
    close();
    setBanner({ kind: 'ok', msg: isAdd ? 'Surgery created' : 'Surgery updated' });
  };

  const askDeactivate = (p) => {
    setConfirm({
      title: 'Deactivate surgery?',
      message: `"${p.name}" will be hidden from new use. Existing references keep working. Re-activate later by editing and toggling Active.`,
      onConfirm: async () => {
        setConfirm(null);
        const r = await deactivateSurgery(p.surgery_id);
        setBanner(
          r.success
            ? { kind: 'ok', msg: 'Surgery deactivated' }
            : { kind: 'err', msg: r.error || 'Deactivate failed' }
        );
      },
    });
  };

  const bg = isDark
    ? 'linear-gradient(135deg, #0E0E0F 0%, #1a1a2e 50%, #0E0E0F 100%)'
    : 'linear-gradient(135deg, #f8fafc 0%, #eef2ff 50%, #f8fafc 100%)';

  const inputCls =
    'w-full rounded-lg px-3 py-2 text-sm border bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-800 dark:text-slate-100';
  const labelCls =
    'block text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1';

  const isPackage = row.billing_mode === 'PACKAGE';

  // B6 / S4 (Variant A) — add/edit form panel. Top for a new item; inline below
  // its source row for an edit. One element, one position per render (add XOR edit).
  const formPanel = form && (
    <div className="pcp-add-wrap">
      <div className="pcp-add-title">
        {form.mode === 'add' ? 'New Surgery' : `Edit · ${orig?.name || ''}`}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <div className="lg:col-span-2">
          <label className={labelCls}>Name *</label>
          <input
            className={inputCls}
            aria-label="Name *"
            value={row.name}
            onChange={(e) => change('name', e.target.value)}
            placeholder="LSCS (Caesarean delivery)"
          />
          {errors.name && <div className="text-xs text-rose-600 mt-1">{errors.name}</div>}
        </div>

        <div>
          <label className={labelCls}>Specialty</label>
          <input
            className={inputCls}
            aria-label="Specialty"
            value={row.specialty}
            onChange={(e) => change('specialty', e.target.value)}
            placeholder="Obstetrics"
          />
          <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            Clinical specialty (label only) — use &ldquo;Visible to&rdquo; below for department visibility.
          </div>
        </div>

        <div>
          <label className={labelCls}>Billing mode</label>
          <select
            className={inputCls}
            aria-label="Billing mode"
            value={row.billing_mode}
            onChange={(e) => change('billing_mode', e.target.value)}
          >
            {BILLING_MODES.map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
          <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            Switching modes keeps what you typed — only the active mode is validated.
          </div>
        </div>

        {isPackage ? (
          <>
            <div>
              <label className={labelCls}>Package price (₹) *</label>
              <input
                className={inputCls}
                type="number"
                min="0"
                aria-label="Package price"
                value={row.package_price}
                onChange={(e) => change('package_price', e.target.value)}
              />
              {errors.package_price && (
                <div className="text-xs text-rose-600 mt-1">{errors.package_price}</div>
              )}
            </div>
            <div>
              <label className={labelCls}>Inclusions (comma-separated)</label>
              <input
                className={inputCls}
                aria-label="Inclusions"
                value={row.inclusions}
                onChange={(e) => change('inclusions', e.target.value)}
                placeholder="OT, 2 bed-days, surgeon fee"
              />
            </div>
          </>
        ) : (
          <div className="lg:col-span-2">
            <label className={labelCls}>Components * (per role)</label>
            <div className="flex flex-wrap gap-4">
              {COMPONENT_ROLES.map((c) => (
                <label
                  key={c.value}
                  className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200"
                >
                  <input
                    type="checkbox"
                    checked={selectedRoles.includes(c.value)}
                    onChange={() => toggleRole(c.value)}
                  />
                  {c.label}
                </label>
              ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
              {selectedRoles.map((role) => {
                const meta = COMPONENT_ROLES.find((c) => c.value === role);
                return (
                  <div key={role}>
                    <label className={labelCls}>{meta?.label || role} fee (₹)</label>
                    <input
                      className={inputCls}
                      type="number"
                      min="0"
                      aria-label={`${meta?.label || role} fee`}
                      value={componentPrices[role] ?? ''}
                      onChange={(e) =>
                        setComponentPrices((prev) => ({ ...prev, [role]: e.target.value }))
                      }
                    />
                  </div>
                );
              })}
            </div>
            {errors.components && (
              <div className="text-xs text-rose-600 mt-1">{errors.components}</div>
            )}
          </div>
        )}

        <div>
          <label className={labelCls}>GST</label>
          <select
            className={inputCls}
            aria-label="GST"
            value={row.gst_rate}
            onChange={(e) => change('gst_rate', e.target.value)}
          >
            {GST_RATES.map((g) => (
              <option key={g} value={g}>{g}%</option>
            ))}
          </select>
        </div>

        <div>
          <label className={labelCls}>Duration (minutes)</label>
          <input
            className={inputCls}
            type="number"
            min="0"
            aria-label="Duration"
            value={row.duration}
            onChange={(e) => change('duration', e.target.value)}
          />
          {errors.duration && <div className="text-xs text-rose-600 mt-1">{errors.duration}</div>}
        </div>

        <div className="flex flex-col gap-2 mt-5">
          <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
            <input
              type="checkbox"
              checked={row.implants_billable}
              onChange={(e) => change('implants_billable', e.target.checked)}
            />
            Implants billable
            <span className="text-xs text-slate-500 dark:text-slate-400">
              (billed at actuals on top — either mode)
            </span>
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
            <input
              type="checkbox"
              checked={row.requires_consent}
              onChange={(e) => change('requires_consent', e.target.checked)}
            />
            Requires consent
          </label>
          {form.mode === 'edit' && (
            <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
              <input
                type="checkbox"
                checked={row.active !== false}
                onChange={(e) => change('active', e.target.checked)}
              />
              Active
            </label>
          )}
        </div>
      </div>

      {/* B5 / S1 — "Visible to" departments (optional, multi). Hidden on a
          dark org. Empty = org-wide. Saved via the PUT in the same Save
          click. Orthogonal to the free-text specialty label above. */}
      {activeDepts.length > 0 && (
        <div className="mt-3">
          <label className={labelCls}>
            Visible to{' '}
            <span className="text-xs font-normal normal-case text-slate-400 dark:text-slate-500">
              (optional, multi — leave empty for org-wide)
            </span>
          </label>
          <div className="flex flex-wrap gap-4">
            {activeDepts.map((d) => (
              <label key={d.dept_id} className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
                <input
                  type="checkbox"
                  checked={deptIds.includes(d.dept_id)}
                  onChange={() => toggleDept(d.dept_id)}
                />
                {d.name}
              </label>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-2 mt-4">
        <button className="pcp-btn pcp-btn-primary" onClick={submit} disabled={busy}>
          {busy ? 'Saving…' : form.mode === 'add' ? 'Create' : 'Save Changes'}
        </button>
        <button className="pcp-btn pcp-btn-secondary" onClick={close} disabled={busy}>
          Cancel
        </button>
      </div>
    </div>
  );

  // Permission gate — all hooks above; redirect home like LabCatalogPage.
  // The server still 403s every write (defence-in-depth, S4 AC-NEG).
  if (!hasPermission('MANAGE_BED_CONFIG')) {
    return <Navigate to={ROUTES.ORG} replace />;
  }

  return (
    <div className="pcp-page" style={{ background: bg }}>
      <AdminPageHeader />

      <main className="pcp-main">
        <div className="pcp-title-row">
          <div>
            <h1 className="pcp-page-title" style={{ color: isDark ? '#f5f5f7' : '#111827' }}>
              🔪 Surgery Catalog
            </h1>
            <p className="pcp-page-subtitle" style={{ color: isDark ? '#9ca3af' : '#6b7280' }}>
              Organisation-wide surgeries, operations &amp; deliveries — package or itemized pricing.
            </p>
          </div>
          <button className="pcp-btn pcp-btn-primary" onClick={openAdd} disabled={!!form}>
            + Add Surgery
          </button>
        </div>
        <FeesBillingTabs active="surgery-catalog" />

        {banner && (
          <div className={`pcp-banner ${banner.kind === 'ok' ? 'pcp-banner-ok' : 'pcp-banner-err'}`}>
            <span>{banner.msg}</span>
            <button onClick={() => setBanner(null)} aria-label="Dismiss">✕</button>
          </div>
        )}

        <div className="pcp-filter-bar">
          <label className="pcp-checkbox">
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
            />
            Show deactivated
          </label>
          {activeDepts.length > 0 && (
            <select
              className={inputCls}
              style={{ width: 'auto' }}
              aria-label="Filter by department"
              value={deptFilter}
              onChange={(e) => setDeptFilter(e.target.value)}
            >
              <option value="">All departments</option>
              <option value={ORG_WIDE_FILTER}>Org-wide</option>
              {activeDepts.map((d) => (
                <option key={d.dept_id} value={d.dept_id}>{d.name}</option>
              ))}
            </select>
          )}
          <span className="pcp-count">{visibleSurgeries.length} surgeries</span>
        </div>

        {form?.mode === 'add' && formPanel}

        {isLoading ? (
          <div className="pcp-state">Loading…</div>
        ) : error ? (
          <div className="pcp-state pcp-state-err">
            {error}
            <button
              className="pcp-btn pcp-btn-secondary"
              onClick={() => { clearError(); fetchSurgeries({ force: true }); }}
            >
              Retry
            </button>
          </div>
        ) : surgeries.length === 0 ? (
          <div className="pcp-state">
            🔪 No surgeries yet. Click + Add Surgery to create the first one.
          </div>
        ) : (
          <table className="pcp-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Billing</th>
                <th>GST</th>
                <th>Flags</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleSurgeries.map((p) => {
                const isEditing = form?.mode === 'edit' && form.id === p.surgery_id;
                return (
                <React.Fragment key={p.surgery_id}>
                <tr
                  className={`${p.active === false ? 'pcp-row pcp-row-inactive' : 'pcp-row'}${isEditing ? ' pcp-row-editing' : ''}`}
                >
                  <td>
                    <div className="pcp-name">{p.name}</div>
                    <div className="pcp-subname">
                      {p.surgery_id}
                      {p.specialty && ` · ${p.specialty}`}
                    </div>
                  </td>
                  <td className="pcp-cost">
                    {p.billing_mode === 'ITEMIZED'
                      ? `Itemized · ${(p.components || []).length} component${(p.components || []).length === 1 ? '' : 's'}`
                      : `Package · ₹${Number(p.package_price || 0).toFixed(2)}`}
                  </td>
                  <td>{p.gst_rate ?? 0}%</td>
                  <td>
                    <div className="pcp-chips">
                      {p.implants_billable && <span className="pcp-chip">Implants billable</span>}
                      {p.requires_consent && <span className="pcp-chip">Consent</span>}
                      {p.duration != null && <span className="pcp-chip">{p.duration} min</span>}
                      {p.is_preset && <span className="pcp-chip">Preset</span>}
                      {/* B5 — dept tags (active edges). See BedCatalogPage for the
                          org-wide / retired-caption semantics (RC-4). */}
                      {(p.department_ids || []).length > 0 ? (
                        <>
                          <span className="pcp-depts-caption">Visible to:</span>
                          {(p.department_ids || []).map((did) => (
                            <span
                              key={did}
                              className="pcp-chip"
                              style={{
                                background: isDark ? '#3730a3' : '#e0e7ff',
                                color: isDark ? '#e0e7ff' : '#3730a3',
                                borderColor: isDark ? '#4f46e5' : '#a5b4fc',
                              }}
                            >
                              🏷 {deptNameById[did] || did}
                            </span>
                          ))}
                        </>
                      ) : activeDepts.length > 0 ? (
                        p.owner_scope === 'DEPARTMENT' ? (
                          <span className="pcp-depts-caption">Org-wide (tagged departments retired)</span>
                        ) : (
                          <span className="pcp-chip">Org-wide</span>
                        )
                      ) : null}
                      {p.active === false && (
                        <span className="pcp-chip pcp-chip-inactive">Deactivated</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="pcp-actions">
                      <button
                        className="pcp-btn pcp-btn-link"
                        onClick={() => openEdit(p)}
                        disabled={!!form}
                      >
                        Edit
                      </button>
                      {p.active !== false && (
                        <button
                          className="pcp-btn pcp-btn-link pcp-btn-danger-link"
                          onClick={() => askDeactivate(p)}
                        >
                          Deactivate
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                {/* B6 / S4 — Variant-A inline edit expands directly below its row */}
                {isEditing && (
                  <tr className="pcp-edit-row">
                    <td colSpan={5}>{formPanel}</td>
                  </tr>
                )}
                </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </main>

      {confirm && (
        <ConfirmationModal
          isOpen={true}
          title={confirm.title}
          message={confirm.message}
          ctaLabel="Deactivate"
          cancelLabel="Cancel"
          variant="danger"
          onConfirm={confirm.onConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
}
